import React, { useEffect, useState } from 'react';
import { WhatsAppButton, useWhatsAppPublic } from '../../components/WhatsApp.jsx';
import { useLive } from '../../live.jsx';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { useCategories } from '../../data.js';
import { pickLang, useContent } from '../../content.jsx';
import { useCatalogue, deptPath, subPath, typePath, specOptions, usePageMeta, chipValue, techOf } from '../../catalogue.js';
import { StatIcon } from '../../components/sectors/SectorShowcase.jsx';
import ListingCard from '../../components/ListingCard.jsx';
import { UNITS } from '../../productOptions.js';
import { Alert, Field } from '../../components/ui.jsx';
import NotFound from '../site/NotFound.jsx';
import CatalogueNav, { CatalogueNavButton, useNavFolded, useNarrow } from './CatalogueNav.jsx';
import './catalogue.css';

// Catalogue pages (FR /secteurs/…, EN /sectors/…), 4 levels from the
// category tree (Admin > Catégories / Secteurs):
//   /:sector                       → opens its first department
//   /:sector/:dept                 → department: photo tiles of its sub-categories
//   /:sector/:dept/:sub            → sub-category: its product types, intro, badges, spec summary
//   /:sector/:dept/:sub/:type      → product type: the detail page
// An empty section is not shown. An address from before the 4 levels
// (e.g. /secteurs/metals/acier-long/tmt) moves to the current one.

const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];
const L = (v, lang) => pickLang(v, lang);
const firstLine = (s) => String(s || '').split('\n')[0];

function Placeholder({ text, hue = 210 }) {
  return <span className="cat-ph" style={{ '--h': hue }} aria-hidden="true">{String(text || '?').slice(0, 1)}</span>;
}

// Catalogue side panel (whole tree) + Back + breadcrumb.
function Shell({ crumbs, sectors, current, children }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [folded, setFolded] = useNavFolded();
  const [drawer, setDrawer] = useState(false);
  const narrow = useNarrow();
  return (
    <div className="cat-page">
      <div className={`container cat-layout${folded && !narrow ? ' is-folded' : ''}`}>
        <CatalogueNav sectors={sectors} current={current} folded={folded} setFolded={setFolded} drawerOpen={drawer} setDrawerOpen={setDrawer} />
        <div className="cat-content">
          <div className="cat-topbar">
            {narrow && <CatalogueNavButton open={drawer} onClick={() => setDrawer(true)} />}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}>← {t('catalogue.back')}</button>
            <nav aria-label={t('catalogue.breadcrumb')}>
              <ol className="breadcrumb">
                {crumbs.map((c, i) => (
                  <li key={i}>{c.to && i < crumbs.length - 1 ? <Link to={c.to}>{c.label}</Link> : <span aria-current={i === crumbs.length - 1 ? 'page' : undefined}>{c.label}</span>}</li>
                ))}
              </ol>
            </nav>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function ViewMore({ text }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const long = text.length > 320 || text.split('\n').length > 4;
  return (
    <div className={`view-more${open || !long ? ' is-open' : ''}`}>
      <p className="pre-line">{text}</p>
      {long && <button type="button" className="btn btn-link btn-sm" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? t('catalogue.viewLess') : t('catalogue.viewMore')}</button>}
    </div>
  );
}

// Applications and notes (e.g. trade requirements) of a page.
function UsesAndNotes({ item }) {
  const { t, lang } = useI18n();
  const apps = L(item?.applications, lang);
  const notes = L(item?.notes, lang);
  if (!apps && !notes) return null;
  return (
    <section className="cat-section cat-uses">
      {apps && <><h2>{t('catalogue.applications')}</h2><p className="cat-apps">{apps}</p></>}
      {notes && <p className="cat-notes callout callout-info small">{notes}</p>}
    </section>
  );
}

function Badges({ badges }) {
  const { lang } = useI18n();
  const list = (badges || []).filter((b) => L(b.text, lang));
  if (!list.length) return null;
  return <ul className="trust-badges">{list.map((b, i) => <li key={i}><StatIcon name={b.icon} size={16} />{L(b.text, lang)}</li>)}</ul>;
}

// "Frequently bought with": chosen product types, or the defaults given.
function Related({ title, ids, fallback, types }) {
  const { lang } = useI18n();
  const chosen = (ids || []).map((id) => types.get(id)).filter(Boolean);
  const list = (chosen.length ? chosen : fallback).slice(0, 10);
  if (!list.length) return null;
  return (
    <section className="cat-section">
      <h2>{title}</h2>
      <ul className="related-strip">
        {list.map(({ s, d, sub, ty }) => (
          <li key={ty.id}>
            <Link to={typePath(s, d, sub, ty, lang)} className="related-item">
              <span className="related-img">{ty.photos?.[0] ? <img src={ty.photos[0]} alt="" loading="lazy" /> : <Placeholder text={L(ty.name, lang)} hue={(ty.id.length * 53) % 360} />}</span>
              <span className="related-name">{L(ty.name, lang)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Approved marketplace listings in the given categories (hidden if none).
function SellerListings({ title, slugs }) {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const key = slugs.join(',');
  const [tick, setTick] = useState(0);
  useLive(['listings'], () => setTick((x) => x + 1));
  useEffect(() => {
    if (!key) { setItems([]); return undefined; }
    let alive = true;
    fetch(`/api/marketplace/listings?category=${encodeURIComponent(key)}`, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : { items: [] })).then((d) => { if (alive) setItems(d.items || []); }).catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, [key, tick]);
  if (!items || !items.length) return null;
  return (
    <section className="cat-section">
      <div className="cat-section-head">
        <h2>{title}</h2>
        <Link to={`/marketplace?category=${key}`} className="sector-more2">{t('catalogue.allListings')} →</Link>
      </div>
      <div className="listing-grid">{items.slice(0, 8).map((l) => <ListingCard key={l.id} listing={l} />)}</div>
    </section>
  );
}

const money = (n, currency, lang) => new Intl.NumberFormat(lang === 'en' ? 'en-CA' : 'fr-CA', { style: 'currency', currency, maximumFractionDigits: n >= 1000 ? 0 : 2 }).format(n);
const day = (iso, lang) => (iso ? new Intl.DateTimeFormat(lang === 'en' ? 'en-CA' : 'fr-CA', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T12:00:00Z`)) : '');

// ── Sector address (/secteurs/:sector) ───────────────────────────────────
// Not found: an older address (before the 4 levels, or another level)
// whose last part names a page of the sector → that page.
function Moved() {
  const { sector, dept, sub, type } = useParams();
  const { lang } = useI18n();
  const { ready } = useContent('sectors');
  const location = useLocation();
  const cat = useCatalogue();
  if (!ready) return null;
  const to = cat.locate(sector, type || sub || dept, lang);
  return to && to !== location.pathname ? <Navigate to={`${to}${location.search}`} replace /> : <NotFound />;
}

const tileHue = (i) => (i * 47 + 20) % 360;

// No sector page of its own yet: opens the sector's first department.
export function SectorPage() {
  const { sector } = useParams();
  const { lang } = useI18n();
  const { ready } = useContent('sectors');
  const cat = useCatalogue();
  if (!ready) return null;
  const s = cat.sectors.find((x) => x.key === sector);
  const d = s && (s.departments || [])[0];
  if (!d) return <NotFound />;
  return <Navigate to={deptPath(s, d, lang)} replace />;
}

// ── Department page (level 2): photo tiles of its sub-categories ─────────
export function DepartmentPage() {
  const { sector, dept } = useParams();
  const { t, lang } = useI18n();
  const cat = useCatalogue();
  const { s, d } = cat.find(sector, dept);
  const known = new Set(useCategories().map((c) => c.slug));
  const subs = d?.subs || [];
  const name = d ? L(d.name, lang) : '';
  const title = d ? L(d.title, lang) || name : '';
  const subtitle = d ? L(d.subtitle, lang) || subs.map((x) => L(x.name, lang)).join(', ') : '';
  usePageMeta(d ? L(d.metaTitle, lang) || `${title} — ${L(s.name, lang)}` : null, d ? L(d.metaDescription, lang) || firstLine(L(d.description, lang)) || subtitle : '');
  if (!d) return <Moved />;
  const listingSlugs = [d.categorySlug].filter((x) => x && known.has(x));
  return (
    <Shell sectors={cat.sectors} current={{ sector: s.key, dept: d.id }} crumbs={[{ to: '/', label: t('nav.home') }, { to: '/#sectors-title', label: L(s.name, lang) }, { label: name }]}>
      <header className="cat-head">
        <h1>{title}</h1>
        {subtitle && <p className="cat-subtitle">{title !== name ? `${name} — ` : ''}{subtitle}</p>}
        <ViewMore text={L(d.description, lang)} />
        <Badges badges={d.badges} />
      </header>
      {subs.length > 0 && (
        <section className="cat-section">
          <ul className="sub-grid">
            {subs.map((sb, i) => {
              const img = sb.photo || (sb.types || []).find((x) => x.photos?.[0])?.photos[0];
              const n = (sb.types || []).filter((x) => !x.hidden).length;
              return (
                <li key={sb.id}>
                  <Link to={subPath(s, d, sb, lang)} className="sub-tile">
                    <span className="sub-tile-img">{img ? <img src={img} alt="" loading="lazy" /> : <Placeholder text={L(sb.name, lang)} hue={tileHue(i)} />}</span>
                    <span className="sub-tile-body">
                      <strong>{L(sb.name, lang)}</strong>
                      {n > 0 && <span className="muted small">{t('catalogue.typesCount', { n })}</span>}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      <SellerListings title={t('catalogue.sellerListings')} slugs={listingSlugs} />
    </Shell>
  );
}

// ── Sub-category page (level 3): its product types + intro, badges, spec summary
export function SubCategoryPage() {
  const { sector, dept, sub: subSlug } = useParams();
  const { t, lang } = useI18n();
  const cat = useCatalogue();
  const { s, d, sub } = cat.find(sector, dept, subSlug);
  const known = new Set(useCategories().map((c) => c.slug));
  const types = (sub?.types || []).filter((x) => !x.hidden);
  const name = sub ? L(sub.name, lang) : '';
  const title = sub ? L(sub.title, lang) || name : '';
  const subtitle = sub ? L(sub.subtitle, lang) || types.map((x) => L(x.name, lang)).join(', ') : '';
  usePageMeta(sub ? L(sub.metaTitle, lang) || `${title} — ${L(d.name, lang)}` : null, sub ? L(sub.metaDescription, lang) || firstLine(L(sub.description, lang)) || subtitle : '');
  if (!sub) return <Moved />;

  const withSpecs = types.filter((x) => (x.specs || []).length);
  const others = d.subs.filter((x) => x.id !== sub.id).flatMap((x) => (x.types || []).filter((y) => !y.hidden).map((ty) => ({ s, d, sub: x, ty })));
  const listingSlugs = [sub.categorySlug, ...types.map((x) => x.categorySlug)].filter((x) => x && known.has(x));
  // Technical content shared by its product types (tables, picture sections).
  const subBlocks = techOf(sub).filter((b) => !b.hidden && (b.kind === 'pictures' ? (b.items || []).length : b.rows?.length && b.headers?.length));

  return (
    <Shell sectors={cat.sectors} current={{ sector: s.key, dept: d.id, sub: sub.id }} crumbs={[{ to: '/', label: t('nav.home') }, { to: '/#sectors-title', label: L(s.name, lang) }, { to: deptPath(s, d, lang), label: L(d.name, lang) }, { label: name }]}>
      <header className="cat-head">
        <h1>{title}</h1>
        {subtitle && <p className="cat-subtitle">{title !== name ? `${name} — ` : ''}{subtitle}</p>}
        <ViewMore text={L(sub.description, lang)} />
        <Badges badges={sub.badges} />
      </header>

      {types.length > 0 && (
        <section className="cat-section">
          <ul className="type-grid">
            {types.map((ty, i) => (
              <li key={ty.id} className="type-card">
                <Link to={typePath(s, d, sub, ty, lang)} className="type-card-img">
                  {ty.photos?.[0] ? <img src={ty.photos[0]} alt="" loading="lazy" /> : <Placeholder text={L(ty.name, lang)} hue={tileHue(i)} />}
                </Link>
                <div className="type-card-body">
                  <h3><Link to={typePath(s, d, sub, ty, lang)}>{L(ty.name, lang)}</Link></h3>
                  {L(ty.description, lang) && <p className="type-card-desc">{L(ty.description, lang)}</p>}
                  <div className="type-card-actions">
                    <Link to={typePath(s, d, sub, ty, lang)} className="btn btn-ghost btn-sm">{t('catalogue.details')}</Link>
                    <Link to={`${typePath(s, d, sub, ty, lang)}?rfq=1`} className="btn btn-primary btn-sm">{t('catalogue.requestQuote')} →</Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {withSpecs.length > 0 && (
        <section className="cat-section">
          <h2>{t('catalogue.howSpecified', { name })}</h2>
          {L(sub.specIntro, lang) && <p className="muted pre-line">{L(sub.specIntro, lang)}</p>}
          <div className="table-scroll">
            <table className="spec-how">
              <thead><tr><th>{t('catalogue.productType')}</th><th>{t('catalogue.specFields')}</th></tr></thead>
              <tbody>
                {withSpecs.map((ty) => (
                  <tr key={ty.id}>
                    <th scope="row"><Link to={typePath(s, d, sub, ty, lang)}>{L(ty.name, lang)}</Link></th>
                    <td>{ty.specs.map((g) => <span key={g.id} className="spec-field">{L(g.label, lang)}</span>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {L(sub.specFooter, lang) && <p className="muted small pre-line">{L(sub.specFooter, lang)}</p>}
        </section>
      )}

      {subBlocks.length > 0 && (
        <section className="cat-section">
          {subBlocks.map((b) => (b.kind === 'pictures'
            ? <PictureSection key={b.id} b={b} level="h2" itemState={noSelect} />
            : <TechTable key={b.id} tb={b} level="h2" rowState={noSelect} />))}
        </section>
      )}
      <UsesAndNotes item={sub} />

      <Related title={t('catalogue.boughtWith')} ids={sub.related} fallback={others} types={cat.types} />
      <SellerListings title={t('catalogue.sellerListings')} slugs={listingSlugs} />
    </Shell>
  );
}
// Tables / picture sections of a sub-category: read only (no quote selection).
const noSelect = () => [false, () => {}];

// Indicative prices (entered by hand in Admin, never live data).
function Prices({ prices, listingSlugs }) {
  const { t, lang } = useI18n();
  const list = (prices || []).filter((p) => L(p.name, lang));
  if (!list.length) return null;
  return (
    <section className="cat-section">
      <h2>{t('catalogue.indicativePrices')}</h2>
      <p className="muted small">{t('catalogue.pricesNote')}</p>
      <ul className="price-cards">
        {list.map((p) => (
          <li key={p.id} className="price-card">
            <h3>{L(p.name, lang)}</h3>
            <p className="price-range">
              {p.min !== null && p.max !== null && p.max !== p.min ? `${money(p.min, p.currency, lang)} – ${money(p.max, p.currency, lang)}` : money(p.min ?? p.max, p.currency, lang)}
              {p.unit && <span className="muted"> /{p.unit}</span>}
            </p>
            {p.updatedAt && <p className="muted small">{t('catalogue.updatedOn', { date: day(p.updatedAt, lang) })}</p>}
            <Link to={listingSlugs.length ? `/marketplace?category=${listingSlugs.join(',')}` : `/marketplace?q=${encodeURIComponent(L(p.name, lang))}`} className="btn btn-ghost btn-sm">{t('catalogue.seeListings')} →</Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Product type page ────────────────────────────────────────────────────
export function ProductTypePage() {
  const { sector, dept, sub: subSlug, type } = useParams();
  const { t, lang } = useI18n();
  const cat = useCatalogue();
  const { s, d, sub, ty } = cat.find(sector, dept, subSlug, type);
  const known = new Set(useCategories().map((c) => c.slug));
  const [photo, setPhoto] = useState(0);
  const [chosen, setChosen] = useState({}); // groupId → value
  const [expanded, setExpanded] = useState({});
  const [picked, setPicked] = useState({}); // tableId → first cell of the clicked row (tables without chips)
  const [rfq, setRfq] = useState(() => new URLSearchParams(window.location.search).get('rfq') === '1');
  const name = ty ? L(ty.name, lang) : '';
  const title = ty ? L(ty.title, lang) || name : '';
  usePageMeta(ty ? L(ty.metaTitle, lang) || `${title} — ${L(sub.name, lang)}` : null, ty ? L(ty.metaDescription, lang) || firstLine(L(ty.description, lang)) || `${name} — ${L(sub.name, lang)}, ${L(d.name, lang)}` : '');
  useEffect(() => { setPhoto(0); setChosen({}); setExpanded({}); setPicked({}); }, [type]);
  if (!ty) return <Moved />;

  const photos = (ty.photos || []).slice(0, 4);
  const badges = (ty.badges || []).length ? ty.badges : sub.badges;
  const siblings = (sub.types || []).filter((x) => !x.hidden && x.id !== ty.id).map((x) => ({ s, d, sub, ty: x }));
  // Its own indicative prices, else its sub-category's.
  const prices = (ty.prices || []).length ? ty.prices : sub.prices;
  const slugs = [ty.categorySlug].filter((x) => x && known.has(x));
  const specs = (ty.specs || []).map((g) => ({ ...g, opts: specOptions(g, lang) })).filter((g) => g.opts.length);
  // Technical content in the admin's order: tables and picture sections.
  const blocks = techOf(ty).filter((b) => !b.hidden && (b.kind === 'pictures' ? (b.items || []).length : b.rows?.length && b.headers?.length));
  const tables = blocks.filter((b) => b.kind === 'table');
  const chosenList = [
    ...specs.filter((g) => chosen[g.id]).map((g) => ({ groupId: g.id, label: L(g.label, lang), value: chosen[g.id], display: g.opts.find((o) => o.value === chosen[g.id])?.label })),
    ...tables.filter((tb) => picked[tb.id]).map((tb) => ({ groupId: `t-${tb.id}`, label: L(tb.headers[0], lang) || L(tb.title, lang), value: picked[tb.id] })),
  ];
  // A clicked table row selects its size: the matching chip when the
  // table's first column is a spec group, else the cell itself.
  const rowState = (tb, row) => {
    const g = specs.find((x) => x.fromTable === tb.id);
    return g ? [chosen[g.id] === chipValue(row[0]), () => setChosen((c) => ({ ...c, [g.id]: c[g.id] === chipValue(row[0]) ? undefined : chipValue(row[0]) }))]
      : [picked[tb.id] === row[0], () => setPicked((p) => ({ ...p, [tb.id]: p[tb.id] === row[0] ? undefined : row[0] }))];
  };
  // Picture items are selectable only when the section's names are chips.
  const itemState = (b, it) => {
    const g = specs.find((x) => x.fromTable === b.id);
    if (!g) return [false, null];
    const v = chipValue(it.name?.fr || it.name?.en);
    return [chosen[g.id] === v, () => setChosen((c) => ({ ...c, [g.id]: c[g.id] === v ? undefined : v }))];
  };

  return (
    <Shell sectors={cat.sectors} current={{ sector: s.key, dept: d.id, sub: sub.id, type: ty.id }} crumbs={[{ to: '/', label: t('nav.home') }, { to: '/#sectors-title', label: L(s.name, lang) }, { to: deptPath(s, d, lang), label: L(d.name, lang) }, { to: subPath(s, d, sub, lang), label: L(sub.name, lang) }, { label: name }]}>
      <div className="type-top">
        <div className="type-gallery">
          <div className="type-main-photo">{photos[photo] ? <img src={photos[photo]} alt={L(ty.photoAlt, lang) || name} /> : <Placeholder text={name} hue={(ty.id.length * 53) % 360} />}</div>
          {photos.length > 1 && (
            <div className="type-thumbs">
              {photos.map((p, i) => (
                <button key={p} type="button" className={i === photo ? 'on' : ''} onClick={() => setPhoto(i)} aria-label={t('listing.photo', { n: i + 1, total: photos.length })} aria-pressed={i === photo}><img src={p} alt="" /></button>
              ))}
            </div>
          )}
        </div>
        <div className="type-info">
          <h1>{title}</h1>
          <ViewMore text={L(ty.description, lang)} />
          <Badges badges={badges} />
          <button type="button" className="btn btn-primary btn-lg rfq-cta" onClick={() => setRfq(true)}>{t('catalogue.requestQuote')} →</button>
          <RfqWhatsApp name={title} />
        </div>
      </div>

      {(specs.length > 0 || blocks.length > 0) && (
        <section className="cat-section">
          {specs.length > 0 && <>
          <h2>{t('catalogue.specsFor', { name })}</h2>
          <p className="muted small">{t('catalogue.specsHelp')}</p>
          <div className="spec-groups">
            {specs.map((g) => {
              const open = expanded[g.id];
              const shown = open ? g.opts : g.opts.slice(0, 5);
              return (
                <div key={g.id} className="spec-group">
                  <span className="spec-label" id={`sg-${g.id}`}>{L(g.label, lang)}</span>
                  <div className="spec-chips" role="radiogroup" aria-labelledby={`sg-${g.id}`}>
                    {shown.map((o) => (
                      <button key={o.value} type="button" role="radio" aria-checked={chosen[g.id] === o.value} className={`spec-chip${chosen[g.id] === o.value ? ' on' : ''}`}
                        onClick={() => setChosen((c) => ({ ...c, [g.id]: c[g.id] === o.value ? undefined : o.value }))}>{o.label}</button>
                    ))}
                    {g.opts.length > 5 && (
                      <button type="button" className="spec-chip spec-more" onClick={() => setExpanded((x) => ({ ...x, [g.id]: !open }))} aria-expanded={!!open}>
                        {open ? t('catalogue.less') : t('catalogue.more', { n: g.opts.length - 5 })}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </>}
          {blocks.map((b) => (b.kind === 'pictures'
            ? <PictureSection key={b.id} b={b} level={specs.length ? 'h3' : 'h2'} itemState={itemState} />
            : <TechTable key={b.id} tb={b} level={specs.length ? 'h3' : 'h2'} rowState={rowState} />))}
          <button type="button" className="btn btn-primary" onClick={() => setRfq(true)}>{t('catalogue.requestQuote')}{chosenList.length ? ` (${chosenList.length})` : ''} →</button>
        </section>
      )}

      <Prices prices={prices} listingSlugs={slugs} />
      <UsesAndNotes item={ty} />
      <Related title={t('catalogue.boughtWithName', { name })} ids={ty.related} fallback={siblings} types={cat.types} />
      <SellerListings title={t('catalogue.sellerListings')} slugs={slugs} />

      {rfq && <RfqModal s={s} d={sub} ty={ty} specs={chosenList} onClose={() => setRfq(false)} />}
    </Shell>
  );
}

// Technical data table: striped, first column sticky when it scrolls
// sideways, 12 rows then "View all (N)". Rows are selectable (quote form).
const TABLE_ROWS = 12;
function TechTable({ tb, level, rowState }) {
  const { t, lang } = useI18n();
  const [all, setAll] = useState(false);
  const H = level;
  const title = L(tb.title, lang);
  const note = L(tb.note, lang);
  const rows = all ? tb.rows : tb.rows.slice(0, TABLE_ROWS);
  return (
    <div className="tech-table-block">
      {title && <H className="tech-table-title">{title}</H>}
      <p className="muted small">{t('catalogue.tableHint')}</p>
      <div className="tech-table-wrap" role="region" aria-label={title || t('catalogue.tableLabel')} tabIndex={0}>
        <table className="tech-table">
          <thead><tr>{tb.headers.map((h, k) => <th key={k} scope="col">{L(h, lang)}</th>)}</tr></thead>
          <tbody>
            {rows.map((row, i) => {
              const [on, toggle] = rowState(tb, row);
              return (
                <tr key={i} className={on ? 'on' : ''} onClick={toggle}>
                  {row.map((c, k) => (k === 0
                    ? <th key={k} scope="row"><button type="button" aria-pressed={on} onClick={(e) => { e.stopPropagation(); toggle(); }}>{c}</button></th>
                    : <td key={k}>{c}</td>))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {tb.rows.length > TABLE_ROWS && (
        <button type="button" className="btn btn-link tech-table-more" onClick={() => setAll((x) => !x)} aria-expanded={all}>
          {all ? t('catalogue.showLess') : t('catalogue.showAll', { n: tb.rows.length })}
        </button>
      )}
      {note && <p className="tech-table-note muted small">{note}</p>}
    </div>
  );
}

// Picture section: thumbnail + name + definition per item. Selectable
// (like table rows) when the section's names are chips.
function PictureSection({ b, level, itemState }) {
  const { t, lang } = useI18n();
  const H = level;
  const title = L(b.title, lang);
  const selectable = !!itemState(b, b.items[0])[1];
  return (
    <div className="pic-section">
      {title && <H className="tech-table-title">{title}</H>}
      {selectable && <p className="muted small">{t('catalogue.picturesHint')}</p>}
      <ul className="pic-list">
        {b.items.map((it) => {
          const [on, toggle] = itemState(b, it);
          const name = L(it.name, lang);
          const body = (
            <>
              {it.photo && <span className="pic-thumb"><img src={it.photo} alt="" loading="lazy" decoding="async" /></span>}
              <span className="pic-text">
                <span className="pic-name">{name}</span>
                {L(it.text, lang) && <span className="pic-def">{L(it.text, lang)}</span>}
              </span>
            </>
          );
          return (
            <li key={it.id} className={`pic-item${on ? ' on' : ''}`}>
              {toggle ? <button type="button" className="pic-row" aria-pressed={on} onClick={toggle}>{body}</button> : <div className="pic-row">{body}</div>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Request a quote ──────────────────────────────────────────────────────
function RfqModal({ s, d, ty, specs, onClose }) {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const product = `${L(d.name, lang)} › ${L(ty.name, lang)}`;
  const [f, setF] = useState({
    quantity: '', unit: 'tonne', city: user?.city || '', province: user?.province || 'QC', requiredDate: '', notes: '',
    name: user?.name || '', company: user?.companyName || '', email: user?.email || '', phone: user?.phone || '', website: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api('/rfq', { method: 'POST', body: {
        ...f, quantity: f.quantity === '' ? null : Number(f.quantity), unit: f.quantity === '' ? '' : t(`units.${f.unit}`),
        productName: product, sectorKey: s.key, departmentId: d.id, typeId: ty.id, lang,
        specs: specs.map((x) => ({ label: x.label, value: x.display || x.value })),
      } });
      setDone(true);
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop rfq-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal rfq-modal" role="dialog" aria-modal="true" aria-labelledby="rfq-title">
        <div className="modal-head">
          <h2 id="rfq-title">{t('catalogue.rfqTitle')}</h2>
          <button type="button" className="btn btn-link" onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>
        <div className="modal-body">
          {done ? (
            <div className="rfq-done">
              <p className="rfq-done-icon" aria-hidden="true">✓</p>
              <h3>{t('catalogue.rfqSentTitle')}</h3>
              <p className="muted">{t('catalogue.rfqSentText')}</p>
              <button type="button" className="btn btn-primary" onClick={onClose}>{t('common.close')}</button>
            </div>
          ) : (
            <form className="form" onSubmit={submit}>
              <div className="rfq-product">
                <strong>{product}</strong>
                {specs.length > 0 && <ul className="rfq-specs">{specs.map((x) => <li key={x.groupId}>{x.label} : <strong>{x.display || x.value}</strong></li>)}</ul>}
              </div>
              <div className="grid-form">
                <Field label={t('catalogue.rfqQuantity')} optional htmlFor="rq-qty">
                  <div className="input-pair">
                    <input id="rq-qty" type="number" min="0" step="any" inputMode="decimal" value={f.quantity} onChange={set('quantity')} />
                    <select aria-label={t('listingForm.unit')} value={f.unit} onChange={set('unit')}>{UNITS.map((u) => <option key={u} value={u}>{t(`units.${u}`)}</option>)}</select>
                  </div>
                </Field>
                <Field label={t('catalogue.rfqDate')} optional htmlFor="rq-date"><input id="rq-date" type="date" value={f.requiredDate} onChange={set('requiredDate')} /></Field>
              </div>
              <div className="grid-form">
                <Field label={t('catalogue.rfqCity')} optional htmlFor="rq-city"><input id="rq-city" value={f.city} onChange={set('city')} maxLength={120} /></Field>
                <Field label={t('company.fields.region')} optional htmlFor="rq-prov">
                  <select id="rq-prov" value={f.province} onChange={set('province')}>
                    {PROVINCES.map((p) => <option key={p} value={p}>{t(`provinces.${p}`)}</option>)}
                    <option value="other">{t('catalogue.rfqOutsideCanada')}</option>
                  </select>
                </Field>
              </div>
              <Field label={t('catalogue.rfqNotes')} optional htmlFor="rq-notes"><textarea id="rq-notes" rows={3} value={f.notes} onChange={set('notes')} maxLength={4000} placeholder={t('catalogue.rfqNotesPlaceholder')} /></Field>
              <div className="grid-form">
                <Field label={t('contact.name')} htmlFor="rq-name"><input id="rq-name" value={f.name} onChange={set('name')} required maxLength={120} autoComplete="name" /></Field>
                <Field label={t('catalogue.rfqCompany')} optional htmlFor="rq-co"><input id="rq-co" value={f.company} onChange={set('company')} maxLength={160} autoComplete="organization" /></Field>
              </div>
              <div className="grid-form">
                <Field label={t('contact.email')} htmlFor="rq-email"><input id="rq-email" type="email" value={f.email} onChange={set('email')} required autoComplete="email" /></Field>
                <Field label={t('contact.phone')} optional htmlFor="rq-phone"><input id="rq-phone" type="tel" value={f.phone} onChange={set('phone')} maxLength={30} autoComplete="tel" /></Field>
              </div>
              <div className="hp-field" aria-hidden="true"><label htmlFor="rq-web">{t('contact.website')}</label><input id="rq-web" tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} /></div>
              <Alert>{error}</Alert>
              <p className="field-hint">{t('catalogue.rfqPrivacy')}</p>
              <div className="editor-actions">
                <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t('common.sending') : t('catalogue.rfqSubmit')}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// "Ask about this product on WhatsApp" (migration 043): PME's number, with
// the product and this page's link in the message.
function RfqWhatsApp({ name }) {
  const { t } = useI18n();
  const wa = useWhatsAppPublic();
  if (!wa?.number) return null;
  return <p style={{ marginTop: 10 }}><WhatsAppButton number={wa.number} label={t('wa.askProduct')} text={t('wa.productMessage', { name, url: window.location.href.split('?')[0] })} /></p>;
}
